import React, { useMemo } from 'react';
import { useCaeIssuesPipeline } from '../../../hooks/analytics/useCaeReports.ts';
import { ProgressBar, WidgetCard } from '../controller/shared.tsx';
import type { CAEIssuesPipelineRow } from '../../../request/analytics/caeReports.ts';

/**
 * Report #11 — Issues Pipeline. Kanban board grouped by `status`.
 *
 * Display-choice note: the docx sketches fixed columns (Draft / Open /
 * Remediation / Closed / Escalated), but the real `sox_issues.status` enum
 * (constants.IssueStatus*) has ~13 values across the review → remediation →
 * verification lifecycle with no "open" value. Columns are derived from the
 * statuses actually present in the data (in enum declaration order) rather
 * than hardcoded, so the board stays correct against the real enum.
 */
const STATUS_ORDER = [
  'draft',
  'reviewed',
  'in_management_review',
  'in_discussion',
  'rework',
  'plan_accepted',
  'remediation_in_progress',
  'remediation_complete',
  'pending_verification',
  'verification_complete',
  'verification_failed',
  'reopened',
  'escalated',
  'closed',
];

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export const IssuesPipelineKanban: React.FC<{ deptFilter?: string | null }> = ({ deptFilter }) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useCaeIssuesPipeline();
  const rows = useMemo(() => {
    const base = data ?? [];
    return deptFilter ? base.filter((r) => (r.department ?? '') === deptFilter) : base;
  }, [data, deptFilter]);

  const columns = useMemo(() => {
    const byStatus = new Map<string, CAEIssuesPipelineRow[]>();
    rows.forEach((row) => {
      const list = byStatus.get(row.status) ?? [];
      list.push(row);
      byStatus.set(row.status, list);
    });
    const known = STATUS_ORDER.filter((s) => byStatus.has(s));
    const unknown = Array.from(byStatus.keys()).filter((s) => !STATUS_ORDER.includes(s));
    return [...known, ...unknown].map((status) => ({ status, items: byStatus.get(status) ?? [] }));
  }, [rows]);

  return (
    <WidgetCard
      title="Issues Pipeline"
      description="Open issues grouped by workflow status, with action plan progress per issue"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load issues pipeline.'}
      isEmpty={!isLoading && !isError && rows.length === 0}
      emptyMessage="No issues recorded."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <div className="analytics-scroll flex gap-4 overflow-x-auto pb-2">
        {columns.map((col) => (
          <div key={col.status} className="min-w-[240px] flex-1">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                {statusLabel(col.status)}
              </h4>
              <span className="text-xs font-medium text-gray-400">{col.items.length}</span>
            </div>
            <div className="analytics-scroll space-y-2 max-h-96 overflow-y-auto">
              {col.items.map((item) => {
                const pct = item.total_actions > 0 ? (100 * item.completed_actions) / item.total_actions : 0;
                return (
                  <div key={item.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{item.department ?? 'No department'}</p>
                    {item.classification && (
                      <span className="inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-medium text-gray-700 mt-1 capitalize">
                        {item.classification}
                      </span>
                    )}
                    {item.total_actions > 0 && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
                          <span>Action plans</span>
                          <span>
                            {item.completed_actions}/{item.total_actions}
                          </span>
                        </div>
                        <ProgressBar pct={pct} colorClass={pct >= 100 ? 'bg-green-500' : 'bg-blue-500'} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </WidgetCard>
  );
};

export default IssuesPipelineKanban;
